"use client";

import { useEffect, useRef, useState, useMemo } from "react";
import { setCustomPageTitle } from "@/lib/page-titles";
import { cn } from "@/lib/utils";
import NextLink from "next/link";
import { SiteNavigation } from "@/components/ui/site-navigation";
import { LandingFooter } from "@/components/ui/footer";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { ChevronRight, Link } from "lucide-react";
import {
  ScatterChart,
  BenchmarkTable,
  BarChart,
} from "@/components/blog/charts";
import { GameBreakdownGrid } from "@/components/blog/arc-agi-3/GameBreakdownGrid";
import { LevelChart } from "@/components/blog/arc-agi-3/LevelChart";
import { PerGameBreakdown } from "@/components/blog/arc-agi-3/PerGameBreakdown";
import { ScoreBreakdown } from "@/components/blog/arc-agi-3/ScoreBreakdown";
import { Replays } from "@/components/blog/arc-agi-3/Replays";
import { env } from "@/env";
import { MuxVideo } from "@/components/ui/mux-video";
import { pathToMuxKey } from "@/lib/video-assets";
import { PromptTextarea } from "@/components/ui/prompt-textarea";

// Blog post metadata
export interface BlogPost {
  slug?: string;
  title: string;
  subtitle: string;
  date: string;
  categories: string[];
  coverImage: string;
}

interface BlogPostClientProps {
  post: BlogPost;
  content: string;
}

// Always dark mode — no theme toggle needed

// Extract headings from markdown content
interface Heading {
  id: string;
  text: string;
  level: number;
}

// Extracted outside the render tree so React never unmounts/remounts
// videos due to parent re-renders (e.g. activeHeading scroll tracking).
function BlogVideo({
  children,
  ...props
}: React.VideoHTMLAttributes<HTMLVideoElement> & {
  children?: React.ReactNode;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const muxKey = pathToMuxKey(props.src as string | undefined);

  const togglePlay = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) {
      el.play();
    } else {
      el.pause();
    }
  };

  // Sync state with actual video events
  const handlePlay = () => setIsPlaying(true);
  const handlePause = () => setIsPlaying(false);

  const videoEl = muxKey ? (
    <MuxVideo
      ref={videoRef}
      assetKey={muxKey}
      autoPlay={false}
      className="w-full h-full object-cover"
      onPlay={handlePlay}
      onPause={handlePause}
    />
  ) : (
    <video
      ref={videoRef}
      muted
      loop
      playsInline
      className="w-full h-full object-cover"
      onPlay={handlePlay}
      onPause={handlePause}
    >
      {children}
    </video>
  );

  return (
    <div
      className="rounded-lg overflow-hidden bg-black relative group cursor-pointer"
      style={{ width: "100%", aspectRatio: "16 / 9", margin: "24px 0" }}
      onClick={togglePlay}
    >
      {videoEl}
      <div
        className={cn(
          "absolute inset-0 flex items-center justify-center transition-opacity duration-200",
          isPlaying ? "opacity-0 group-hover:opacity-100" : "opacity-100"
        )}
      >
        <div className="rounded-full bg-black/60 backdrop-blur-sm p-4">
          {isPlaying ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
              <rect x="6" y="4" width="4" height="16" rx="1" />
              <rect x="14" y="4" width="4" height="16" rx="1" />
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </div>
      </div>
    </div>
  );
}

const headingSizes = {
  1: {
    className:
      "font-arizona-sans text-[32px] font-medium leading-[1.2] tracking-[-0.5px] mt-12 mb-6 first:mt-0 group flex items-center gap-2 text-white",
    icon: "w-5 h-5",
  },
  2: {
    className:
      "font-arizona-sans text-[26px] font-medium leading-[1.25] tracking-[-0.3px] mt-10 mb-4 group flex items-center gap-2 text-white/80",
    icon: "w-4 h-4",
  },
  3: {
    className:
      "font-arizona-sans text-[20px] font-medium leading-[1.3] mt-8 mb-3 group flex items-center gap-2 text-white/60",
    icon: "w-4 h-4",
  },
} as const;

function BlogHeading({
  level,
  children,
}: {
  level: 1 | 2 | 3;
  children?: React.ReactNode;
}) {
  const [copied, setCopied] = useState(false);
  const text = String(children);
  const id = text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
  const Tag = `h${level}` as const;
  const { className, icon } = headingSizes[level];

  const handleCopy = () => {
    const url = `${window.location.origin}${window.location.pathname}#${id}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Tag id={id} className={className}>
      {children}
      <span className="relative">
        <button
          onClick={handleCopy}
          className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1 rounded hover:bg-periwinkle/20 text-white/40 hover:text-white/70"
          title="Copy link to section"
        >
          <Link className={icon} />
        </button>
        {copied && (
          <span className="absolute left-full ml-2 top-1/2 -translate-y-1/2 text-periwinkle font-ibm-plex-mono text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
            COPIED
          </span>
        )}
      </span>
    </Tag>
  );
}

function BlogCodeBlock({
  className,
  children,
}: {
  className?: string;
  children?: React.ReactNode;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const match = /language-(\w+)/.exec(className || "");
  const fullMatch = /language-(\w+)(?::(.+))?/.exec(className || "");
  const language = fullMatch?.[1] || match?.[1];
  let caption = fullMatch?.[2];

  if (!caption) {
    const spaceMatch = /language-(\w+)\s+(.+)/.exec(className || "");
    caption = spaceMatch?.[2];
  }

  let codeContent = String(children);
  if (!caption) {
    const captionMatch = /^(?:#|\/\/)\s*caption:\s*(.+)\n/i.exec(codeContent);
    if (captionMatch?.[1]) {
      caption = captionMatch[1].trim();
      codeContent = codeContent.replace(/^(?:#|\/\/)\s*caption:.+\n/i, "");
    }
  }

  const lines = codeContent.split("\n");
  const lineCount = lines.length;
  const isCollapsible = lineCount > 10;
  const displayContent =
    isCollapsible && !isExpanded ? lines.slice(0, 10).join("\n") : codeContent;

  return (
    <div className="my-5">
      {caption && (
        <div className="font-ibm-plex-mono text-[11px] px-4 py-2 rounded-t-lg border-b bg-[#1a1a1a] text-white/60 border-white/10">
          {caption.replace(/_/g, " ")}
        </div>
      )}
      <div className="relative">
        <div
          className={cn(
            "relative overflow-hidden border border-white/10",
            caption ? "rounded-b-xl" : "rounded-lg"
          )}
          style={{ borderTop: caption ? "none" : undefined }}
        >
          <SyntaxHighlighter
            style={oneDark}
            language={language}
            PreTag="div"
            customStyle={{
              margin: "0",
              borderRadius: "0",
              border: "none",
              maxWidth: "100%",
              overflowX: "auto",
              paddingBottom: isCollapsible ? "3rem" : "1em",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              background: "#1c1f26",
            }}
          >
            {displayContent.replace(/\n$/, "")}
          </SyntaxHighlighter>
          {isCollapsible && !isExpanded && (
            <div className="absolute bottom-12 left-0 right-0 h-24 pointer-events-none bg-gradient-to-t from-[#1c1f26] via-[#1c1f26]/60 to-transparent" />
          )}
        </div>
        {isCollapsible && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="absolute bottom-0 left-0 right-0 h-12 font-ibm-plex-mono text-[11px] font-medium uppercase tracking-wider transition-colors text-periwinkle hover:text-periwinkle/80 bg-[#1c1f26]"
            style={{ borderRadius: "0 0 0.5rem 0.5rem" }}
          >
            {isExpanded
              ? "Show Less"
              : `Show More (${lineCount - 10} more lines)`}
          </button>
        )}
      </div>
    </div>
  );
}

function extractHeadings(markdown: string): Heading[] {
  // Remove code blocks (both ``` and ````) to avoid extracting # comments from code
  const withoutCodeBlocks = markdown
    .replace(/````[\s\S]*?````/g, "") // Remove quadruple backtick blocks first
    .replace(/```[\s\S]*?```/g, ""); // Then remove triple backtick blocks

  const headingRegex = /^(#{1,3})\s+(.+)$/gm;
  const headings: Heading[] = [];
  let match;

  while ((match = headingRegex.exec(withoutCodeBlocks)) !== null) {
    const hashes = match[1];
    const text = match[2];
    if (!hashes || !text) continue;

    const level = hashes.length;
    const id = text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
    headings.push({ id, text, level });
  }

  return headings;
}

// Custom light theme for syntax highlighter
const lightCodeTheme: Record<string, React.CSSProperties> = {
  'code[class*="language-"]': {
    color: "#1a1a1a",
    background: "none",
    fontFamily:
      'var(--font-ibm-plex-mono), Consolas, Monaco, "Andale Mono", monospace',
    fontSize: "0.875em",
    textAlign: "left",
    whiteSpace: "pre",
    wordSpacing: "normal",
    wordBreak: "normal",
    wordWrap: "normal",
    lineHeight: "1.6",
    tabSize: 4,
    hyphens: "none",
  },
  'pre[class*="language-"]': {
    color: "#1a1a1a",
    background: "#f8f7f2",
    fontFamily:
      'var(--font-ibm-plex-mono), Consolas, Monaco, "Andale Mono", monospace',
    fontSize: "0.875em",
    textAlign: "left",
    whiteSpace: "pre",
    wordSpacing: "normal",
    wordBreak: "normal",
    wordWrap: "normal",
    lineHeight: "1.6",
    tabSize: 4,
    hyphens: "none",
    padding: "1em",
    margin: "0",
    overflow: "auto",
    borderRadius: "0.5rem",
  },
  comment: { color: "#6a737d" },
  prolog: { color: "#6a737d" },
  doctype: { color: "#6a737d" },
  cdata: { color: "#6a737d" },
  punctuation: { color: "#393a34" },
  property: { color: "#0550ae" },
  tag: { color: "#116329" },
  boolean: { color: "#0550ae" },
  number: { color: "#0550ae" },
  constant: { color: "#0550ae" },
  symbol: { color: "#0550ae" },
  deleted: { color: "#b31d28" },
  selector: { color: "#6639ba" },
  "attr-name": { color: "#6639ba" },
  string: { color: "#0a3069" },
  char: { color: "#0a3069" },
  builtin: { color: "#6639ba" },
  inserted: { color: "#116329" },
  operator: { color: "#cf222e" },
  entity: { color: "#6639ba", cursor: "help" },
  url: { color: "#0550ae" },
  variable: { color: "#953800" },
  atrule: { color: "#0550ae" },
  "attr-value": { color: "#0a3069" },
  function: { color: "#8250df" },
  "class-name": { color: "#116329" },
  keyword: { color: "#cf222e" },
  regex: { color: "#0a3069" },
  important: { color: "#cf222e", fontWeight: "bold" },
  bold: { fontWeight: "bold" },
  italic: { fontStyle: "italic" },
};

function getComponents(): Components {
  const isDark = true;
  return {
    // Headings
    h1: ({ children }) => <BlogHeading level={1}>{children}</BlogHeading>,
    h2: ({ children }) => <BlogHeading level={2}>{children}</BlogHeading>,
    h3: ({ children }) => <BlogHeading level={3}>{children}</BlogHeading>,
    h4: ({ children }) => (
      <h4
        className={cn(
          "font-arizona-sans text-[17px] font-medium leading-[1.4] mt-6 mb-2",
          isDark ? "text-white/60" : "text-[#0a0a0a]/60"
        )}
      >
        {children}
      </h4>
    ),
    h5: ({ children }) => (
      <h5
        className={cn(
          "font-arizona-sans text-[15px] font-medium leading-[1.4] mt-5 mb-2",
          isDark ? "text-white/60" : "text-[#0a0a0a]/60"
        )}
      >
        {children}
      </h5>
    ),
    h6: ({ children }) => (
      <h6
        className={cn(
          "font-arizona-sans text-[14px] font-medium leading-[1.4] mt-4 mb-2 uppercase tracking-wide",
          isDark ? "text-white/60" : "text-[#0a0a0a]/60"
        )}
      >
        {children}
      </h6>
    ),
    // Paragraph
    p: ({ children }) => (
      <p
        className={cn(
          "font-arizona-sans text-[17px] leading-[1.75] mb-5",
          isDark ? "text-white/85" : "text-[#0a0a0a]/85"
        )}
      >
        {children}
      </p>
    ),
    // Links
    a: ({ href, children, className, ...rest }) => {
      // Demo card: routes a relative path through the Agentica base URL so
      // the same href works across dev/prod, and renders as a thumbnail
      // card matching the in-app demo grid.
      if (className === "demo-card") {
        const attrs = rest as Record<string, string | undefined>;
        const image = attrs["data-image"];
        const wideImage = attrs["data-wide-image"];
        const title = attrs["data-title"];
        const tags = attrs["data-tags"];
        const target = href?.startsWith("http")
          ? href
          : `${env.NEXT_PUBLIC_AGENTICA_BASE_URL}${href ?? ""}`;
        return (
          <a
            href={target}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "not-prose group flex flex-col overflow-hidden rounded-2xl border transition-colors",
              isDark
                ? "bg-[#0f0f0f] border-white/10 hover:border-white/25"
                : "bg-white border-black/10 hover:border-black/25"
            )}
          >
            {image ? (
              <div
                className={cn(
                  "aspect-[16/10] w-full overflow-hidden",
                  wideImage && "demo-card-aspect",
                  isDark ? "bg-black" : "bg-[#f5f5f5]"
                )}
              >
                <img
                  src={image}
                  alt={title ?? ""}
                  className={cn(
                    "w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]",
                    wideImage && "demo-card-img"
                  )}
                />
                {wideImage ? (
                  <img
                    src={wideImage}
                    alt={title ?? ""}
                    className="demo-card-wide-img hidden w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                  />
                ) : null}
              </div>
            ) : null}
            <div className="px-5 py-4 flex flex-col gap-1.5">
              {title ? (
                <span
                  className={cn(
                    "font-arizona-sans text-[18px] leading-tight",
                    isDark ? "text-white" : "text-[#0a0a0a]"
                  )}
                >
                  {title}
                </span>
              ) : null}
              {tags ? (
                <span
                  className={cn(
                    "font-ibm-plex-mono text-[11px] uppercase tracking-wider",
                    isDark ? "text-white/40" : "text-black/45"
                  )}
                >
                  {tags}
                </span>
              ) : null}
            </div>
          </a>
        );
      }
      return (
        <a
          href={href}
          className="font-arizona-sans text-periwinkle underline decoration-periwinkle/40 underline-offset-2 hover:decoration-periwinkle transition-colors"
          target={href?.startsWith("http") ? "_blank" : undefined}
          rel={href?.startsWith("http") ? "noopener noreferrer" : undefined}
        >
          {children}
        </a>
      );
    },
    // Lists
    ul: ({ children }) => (
      <ul
        className={cn(
          "font-arizona-sans text-[17px] leading-[1.75] mb-5 pl-6 list-disc marker:text-periwinkle",
          isDark ? "text-white/85" : "text-[#0a0a0a]/85"
        )}
      >
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol
        className={cn(
          "font-arizona-sans text-[17px] leading-[1.75] mb-5 pl-6 list-decimal",
          isDark
            ? "text-white/85 marker:text-white/60"
            : "text-[#0a0a0a]/85 marker:text-[#0a0a0a]/60"
        )}
      >
        {children}
      </ol>
    ),
    li: ({ children }) => <li className="mb-2 pl-1">{children}</li>,
    // Blockquote
    blockquote: ({ children }) => (
      <blockquote
        className={cn(
          "font-arizona-sans text-[17px] leading-[1.75] pl-5 my-6 border-l-2 italic",
          isDark
            ? "border-white/20 text-white/70"
            : "border-[#0a0a0a]/20 text-[#0a0a0a]/70"
        )}
      >
        {children}
      </blockquote>
    ),
    // Inline code & code blocks
    code: ({ className, children, ...props }) => {
      const match = /language-(\w+)/.exec(className || "");
      const isInline = !match;

      if (isInline) {
        return (
          <code
            className={cn(
              "font-ibm-plex-mono text-[14px] px-1.5 py-0.5 rounded",
              isDark
                ? "bg-white/10 text-white/90"
                : "bg-[#0a0a0a]/5 text-[#0a0a0a]/90"
            )}
            {...props}
          >
            {children}
          </code>
        );
      }

      return <BlogCodeBlock className={className}>{children}</BlogCodeBlock>;
    },
    // Pre (code block wrapper)
    pre: ({ children }) => <>{children}</>,
    // Div wrapper - check for side-by-side code blocks and chat comparisons
    div: ({ className, children, ...props }) => {
      // Check if this is a side-by-side code container
      if (className === "code-side-by-side") {
        // Convert children to array to handle both single and multiple children
        const childArray = Array.isArray(children) ? children : [children];

        // Filter out text nodes (like whitespace) and keep only actual elements
        const codeBlocks = childArray.filter((child) => {
          // Keep elements, filter out strings that are just whitespace
          if (typeof child === "string") {
            return child.trim().length > 0;
          }
          return child != null;
        });

        return (
          <div className="flex flex-wrap gap-4 my-5" {...props}>
            {codeBlocks.map((block, idx) => (
              <div key={idx} className="min-w-[340px] flex-1">
                {block}
              </div>
            ))}
          </div>
        );
      }
      // Chat comparison container
      if (className === "chat-comparison") {
        return (
          <div className="flex flex-wrap gap-4 my-6" {...props}>
            {children}
          </div>
        );
      }
      // Chat panel
      if (className === "chat-panel") {
        return (
          <div
            className={cn(
              "min-w-[320px] flex-1 rounded-lg border overflow-hidden",
              isDark
                ? "bg-[#141414] border-white/10"
                : "bg-[#faf9f4] border-black/10"
            )}
            {...props}
          >
            {children}
          </div>
        );
      }
      // Chat label
      if (className === "chat-label") {
        return (
          <div
            className={cn(
              "font-ibm-plex-mono text-[11px] font-medium uppercase tracking-wider px-4 py-2.5 border-b",
              isDark
                ? "bg-[#1a1a1a] text-white/60 border-white/10"
                : "bg-[#f0eee3] text-black/60 border-black/10"
            )}
            {...props}
          >
            {children}
          </div>
        );
      }
      // Chat messages
      if (className?.startsWith("chat-message")) {
        const type = className.split(" ")[1]; // user, agent, code, tool-result

        const baseStyles =
          "px-4 py-2 text-[14px] font-arizona-sans leading-relaxed";

        if (type === "user") {
          return (
            <div
              className={cn(
                baseStyles,
                "border-b",
                isDark
                  ? "bg-periwinkle/10 text-white/90 border-white/5"
                  : "bg-periwinkle/10 text-black/90 border-black/5"
              )}
              {...props}
            >
              <span
                className={cn(
                  "font-ibm-plex-mono text-[10px] font-medium uppercase tracking-wider block mb-1",
                  isDark ? "text-periwinkle/80" : "text-periwinkle"
                )}
              >
                User
              </span>
              {children}
            </div>
          );
        }

        if (type === "agent") {
          return (
            <div
              className={cn(
                baseStyles,
                "border-b",
                isDark
                  ? "text-white/85 border-white/5"
                  : "text-black/85 border-black/5"
              )}
              {...props}
            >
              <span
                className={cn(
                  "font-ibm-plex-mono text-[10px] font-medium uppercase tracking-wider block mb-1",
                  isDark ? "text-emerald-400/80" : "text-emerald-600"
                )}
              >
                Agent
              </span>
              {children}
            </div>
          );
        }

        if (type === "code" || type === "tool-call") {
          return (
            <div
              className={cn(
                "px-4 py-2 border-b font-ibm-plex-mono text-[12px]",
                isDark
                  ? "bg-[#1a1a1a] text-amber-300/90 border-white/5"
                  : "bg-[#f5f4ef] text-amber-700 border-black/5"
              )}
              {...props}
            >
              <span
                className={cn(
                  "font-ibm-plex-mono text-[10px] font-medium uppercase tracking-wider block mb-1",
                  isDark ? "text-amber-400/60" : "text-amber-600/80"
                )}
              >
                {type === "code" ? "Code" : "Tool Calls"}
              </span>
              {children}
            </div>
          );
        }

        if (type === "tool-result" || type === "execution") {
          return (
            <div
              className={cn(
                "px-4 py-2 border-b font-ibm-plex-mono text-[12px]",
                isDark
                  ? "bg-[#0f1410] text-emerald-300/80 border-white/5"
                  : "bg-[#f4f9f5] text-emerald-700 border-black/5"
              )}
              {...props}
            >
              <span
                className={cn(
                  "font-ibm-plex-mono text-[10px] font-medium uppercase tracking-wider block mb-1",
                  isDark ? "text-emerald-400/60" : "text-emerald-600/80"
                )}
              >
                Result
              </span>
              {children}
            </div>
          );
        }

        // Default message style
        return (
          <div
            className={cn(
              baseStyles,
              "border-b",
              isDark
                ? "text-white/85 border-white/5"
                : "text-black/85 border-black/5"
            )}
            {...props}
          >
            {children}
          </div>
        );
      }
      // Demo card grid — 2 columns on md+, 1 column on mobile.
      if (className === "demo-grid") {
        return (
          <div
            className="not-prose my-6 grid grid-cols-1 md:grid-cols-2 gap-4 [&>:only-child]:col-span-full md:[&>:only-child_.demo-card-img]:hidden md:[&>:only-child_.demo-card-wide-img]:block md:[&>:only-child_.demo-card-aspect]:aspect-[16/5]"
            {...props}
          >
            {children}
          </div>
        );
      }
      // Spacers — "spacer" (big) and "spacer-sm" (small)
      if (className === "spacer") {
        return <div className="h-3 md:h-4" />;
      }
      if (className === "spacer-sm") {
        return <div className="h-1.5 md:h-2" />;
      }
      // Agentica CTA box
      if (className === "agentica-cta") {
        return (
          <div className="my-8 flex flex-col gap-6" {...props}>
            <div className="flex items-center gap-2.5">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#7678E3] opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#7678E3]" />
              </span>
              <span className="text-xs font-ibm-plex-mono font-normal tracking-wide uppercase text-white">
                Agentica: Beta Available Now
              </span>
            </div>
            <PromptTextarea
              onSubmit={(message) => {
                window.location.href = `${env.NEXT_PUBLIC_AGENTICA_BASE_URL}?q=${encodeURIComponent(message)}`;
              }}
            />
          </div>
        );
      }
      // Custom chart components
      if (className === "chart-scatter") {
        return <ScatterChart />;
      }
      if (className === "chart-table") {
        return <BenchmarkTable />;
      }
      if (className === "chart-bar") {
        return <BarChart />;
      }
      if (className === "game-breakdown") {
        return <GameBreakdownGrid />;
      }
      if (className === "level-chart") {
        return <LevelChart />;
      }
      if (className === "per-game-breakdown") {
        return <PerGameBreakdown />;
      }
      if (className === "score-breakdown") {
        return <ScoreBreakdown />;
      }
      if (className === "replays") {
        return <Replays />;
      }
      if (className === "agentica-chat-cta") {
        return (
          <div className="my-8">
            <PromptTextarea
              className="max-w-2xl"
              onSubmit={(message) => {
                window.location.href = `${env.NEXT_PUBLIC_AGENTICA_BASE_URL}?q=${encodeURIComponent(message)}`;
              }}
            />
          </div>
        );
      }
      // Default div rendering
      return (
        <div className={className} {...props}>
          {children}
        </div>
      );
    },
    video: BlogVideo,
    source: ({ ...props }) => <source {...props} />,
    // Horizontal rule
    hr: () => (
      <hr
        className={cn(
          "my-12 border-t",
          isDark ? "border-white/10" : "border-[#0a0a0a]/10"
        )}
      />
    ),
    // Strong/Bold
    strong: ({ children }) => (
      <strong
        className={cn(
          "font-arizona-sans font-semibold",
          isDark ? "text-white" : "text-[#0a0a0a]"
        )}
      >
        {children}
      </strong>
    ),
    // Emphasis/Italic
    em: ({ children }) => (
      <em className="font-arizona-sans italic">{children}</em>
    ),
    // Figure captions
    figcaption: ({ children }) => (
      <figcaption
        className={cn(
          "font-arizona-sans text-[14px] text-left mt-4 mb-8",
          isDark ? "text-white/50" : "text-[#0a0a0a]/50"
        )}
      >
        {children}
      </figcaption>
    ),
    // Images
    img: ({ src, alt, className, ...props }) => {
      // Check if this is a theme-specific image
      const srcStr = typeof src === "string" ? src : "";
      const isDarkImage =
        srcStr.includes("DARK_MODE") || srcStr.includes("BLOG1");
      const isLightImage =
        srcStr.includes("LIGHT_MODE") || srcStr.includes("BLOG2");

      // Hide dark images in light mode and vice versa
      if ((isDarkImage && !isDark) || (isLightImage && isDark)) {
        return null;
      }

      return (
        <span className="block my-8 -mx-6 sm:mx-0">
          <img
            src={src}
            alt={alt || ""}
            className={cn(
              "max-w-full rounded-lg",
              isDark ? "opacity-90" : "opacity-100",
              className
            )}
            {...props}
          />
          {alt && (
            <span
              className={cn(
                "font-arizona-sans text-[14px] block text-left mt-3 px-6 sm:px-0",
                isDark ? "text-white/50" : "text-[#0a0a0a]/50"
              )}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  p: ({ children }) => <>{children}</>,
                  code: ({ children }) => (
                    <code
                      className={cn(
                        "font-ibm-plex-mono text-[13px] px-1.5 py-0.5 rounded",
                        isDark
                          ? "bg-white/10 text-white/90"
                          : "bg-[#0a0a0a]/5 text-[#0a0a0a]/90"
                      )}
                    >
                      {children}
                    </code>
                  ),
                }}
              >
                {alt}
              </ReactMarkdown>
            </span>
          )}
        </span>
      );
    },
    // Iframes (for interactive charts with light/dark mode)
    iframe: ({ src, className, ...props }) => {
      const srcStr = typeof src === "string" ? src : "";
      const isDarkIframe = srcStr.includes("DARK_MODE");
      const isLightIframe = srcStr.includes("LIGHT_MODE");

      // Hide dark iframes in light mode and vice versa
      if ((isDarkIframe && !isDark) || (isLightIframe && isDark)) {
        return null;
      }

      return (
        <span className="block my-8 -mx-6 sm:mx-0">
          <iframe
            src={src}
            className={cn("max-w-full rounded-lg", className)}
            {...props}
          />
        </span>
      );
    },
    // Tables
    table: ({ children }) => (
      <div className="overflow-x-auto my-6">
        <table
          className={cn(
            "w-full font-arizona-sans text-[15px] border-collapse",
            isDark ? "text-white/85" : "text-[#0a0a0a]/85"
          )}
        >
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => (
      <thead
        className={cn(
          "border-b",
          isDark ? "border-white/20" : "border-[#0a0a0a]/20"
        )}
      >
        {children}
      </thead>
    ),
    tbody: ({ children }) => <tbody>{children}</tbody>,
    tr: ({ children }) => (
      <tr
        className={cn(
          "border-b",
          isDark ? "border-white/10" : "border-[#0a0a0a]/10"
        )}
      >
        {children}
      </tr>
    ),
    th: ({ children }) => (
      <th
        className={cn(
          "font-arizona-sans font-medium text-left py-3 px-4",
          isDark ? "text-white" : "text-[#0a0a0a]"
        )}
      >
        {children}
      </th>
    ),
    td: ({ children }) => <td className="py-3 px-4">{children}</td>,
    // Delete/strikethrough
    del: ({ children }) => (
      <del className={cn(isDark ? "text-white/50" : "text-[#0a0a0a]/50")}>
        {children}
      </del>
    ),
  } as Components;
}
const blogComponents = getComponents();

export default function BlogPostClient({ post, content }: BlogPostClientProps) {
  const [activeHeading, setActiveHeading] = useState<string>("");
  const [isTocCollapsed, setIsTocCollapsed] = useState<boolean>(true);

  // Always dark — no light mode on blog
  const isDark = true;

  // Add responsive image styles
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .responsive-chart {
        width: 100%;
        margin: 0 auto;
        display: block;
      }
      @media (min-width: 768px) {
        .responsive-chart {
          width: 70%;
        }
      }
      .responsive-diagram {
        width: 100%;
        margin: 0 auto;
        display: block;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    if (post?.title) {
      setCustomPageTitle(`Symbolica Blog – ${post.title}`);
    }
  }, [post?.title]);

  const headings = useMemo(() => extractHeadings(content), [content]);

  // Track active heading on scroll
  useEffect(() => {
    if (!content || headings.length === 0) return;

    const handleScroll = () => {
      const headingElements = document.querySelectorAll(
        "h1[id], h2[id], h3[id]"
      );
      if (headingElements.length === 0) return;

      // Threshold: how far from top of viewport a heading should be to be considered "active"
      const scrollThreshold = 120;

      let currentActiveId = "";

      // Find the heading that's closest to (but above) the scroll threshold
      for (const element of headingElements) {
        const rect = element.getBoundingClientRect();
        // If the heading is above or at the threshold, it's a candidate
        if (rect.top <= scrollThreshold) {
          currentActiveId = element.id;
        } else {
          // Once we hit a heading below the threshold, stop checking
          // (headings are in DOM order, so we found the last one above threshold)
          break;
        }
      }

      // If no heading is above threshold (we're at the very top),
      // highlight the first heading if it's visible
      if (!currentActiveId && headingElements.length > 0) {
        const firstHeading = headingElements[0];
        if (firstHeading) {
          const rect = firstHeading.getBoundingClientRect();
          if (rect.top < window.innerHeight) {
            currentActiveId = firstHeading.id;
          }
        }
      }

      setActiveHeading(currentActiveId);
    };

    // Run once on mount and whenever content changes
    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [content, headings]);

  return (
    <main className="grain relative min-h-screen bg-[#09090a] text-white font-arizona-sans antialiased selection:bg-[#7678E3]/30 agentica-page-fade-in">
      <SiteNavigation isDark={isDark} activePage="blog" />

      {/* Back arrow */}
      <div className="pt-28 px-6 sm:px-10">
        <div className="max-w-7xl mx-auto">
          <NextLink
            href="/blog"
            className="inline-flex items-center gap-2 text-sm text-white/40 hover:text-white transition-colors duration-200"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M7 17l-4-4m0 0l4-4m-4 4h18"
              />
            </svg>
            Back to Blog
          </NextLink>
        </div>
      </div>

      {/* Hero Section */}
      <header className="relative mx-auto max-w-4xl px-6 pt-8 pb-12 text-center">
        {/* Date and Categories */}
        <div className="font-ibm-plex-mono mb-8 text-[12px] tracking-[0.2em] uppercase text-white/60">
          {post.date.toUpperCase()} &middot;{" "}
          {post.categories.map((c) => c.toUpperCase()).join(" · ")}
        </div>

        {/* Title */}
        <h1 className="font-arizona-sans text-[42px] leading-[1.1] tracking-[-1.5px] md:text-[56px] lg:text-[64px] text-white">
          {post.title}
        </h1>

        {/* Subtitle */}
        <p className="font-arizona-sans mt-6 text-[18px] leading-relaxed md:text-[20px] text-white/70 whitespace-pre-line">
          {post.subtitle}
        </p>
      </header>

      {/* Two Column Layout */}
      <div className="mx-auto max-w-[58rem] px-6 pb-24 relative">
        {/* TOC Sidebar - positioned absolutely in margin */}
        <div
          className="hidden lg:block absolute -left-12 top-0 pointer-events-none"
          style={{ height: "calc(100% + 6rem)" }}
        >
          <div className="sticky top-24 pointer-events-auto flex items-start gap-4">
            {/* Left column: TOC Button + Theme Toggle */}
            <div className="flex flex-col items-center gap-4">
              {/* TOC Toggle Button */}
              <button
                onClick={() => setIsTocCollapsed(!isTocCollapsed)}
                className={cn(
                  "font-ibm-plex-mono text-[10px] uppercase tracking-wider transition-all duration-200 px-2 py-4 rounded flex flex-col items-center gap-2 text-periwinkle",
                  isDark ? "hover:bg-white/5" : "hover:bg-black/5"
                )}
                aria-label={
                  isTocCollapsed
                    ? "Expand table of contents"
                    : "Collapse table of contents"
                }
              >
                <span style={{ writingMode: "vertical-rl" }}>
                  Table of Contents
                </span>
                <ChevronRight
                  className={cn(
                    "w-3 h-3 transition-transform duration-300",
                    !isTocCollapsed && "rotate-180"
                  )}
                />
              </button>
            </div>

            {/* Right column: TOC Links */}
            <nav
              className={cn(
                "flex flex-col gap-3 transition-all duration-300",
                isTocCollapsed
                  ? "w-0 opacity-0 overflow-hidden"
                  : "w-[220px] opacity-100"
              )}
            >
              {headings.map((heading) => (
                <a
                  key={heading.id}
                  href={`#${heading.id}`}
                  className={cn(
                    "font-arizona-sans text-[15px] leading-snug transition-colors duration-200 whitespace-nowrap",
                    heading.level === 1 && "font-medium",
                    heading.level === 2 && "font-medium",
                    heading.level === 3 && "pl-4",
                    activeHeading === heading.id
                      ? isDark
                        ? "text-white"
                        : "text-[#0a0a0a]"
                      : isDark
                        ? "text-white/50 hover:text-white/80"
                        : "text-black/50 hover:text-black/80"
                  )}
                >
                  {heading.text}
                </a>
              ))}
            </nav>
          </div>
        </div>

        <div className="flex gap-16">
          {/* Ghost spacer for layout - invisible but takes up space */}
          <div
            className={cn(
              "hidden lg:block transition-all duration-300 shrink-0",
              isTocCollapsed ? "w-0" : "w-[240px]"
            )}
            aria-hidden="true"
          />

          {/* Article Content */}
          <article className="max-w-none min-w-0 flex-1">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={blogComponents}
            >
              {content}
            </ReactMarkdown>
          </article>
        </div>
      </div>

      {/* Footer */}
      <LandingFooter colorMode="dark" noMargin />
    </main>
  );
}
